import React, {ReactNode} from "react";
import {ExampleConditionDef, ExampleQuestionDef, GeneralPrompts} from "../model";

interface ModalSpec {
    title: string;
    content: ReactNode;
    image?: boolean;
}

interface ExamplesViewState {
    questions: ExampleQuestionDef[] | null;
    prompts: GeneralPrompts | null;
    index: number;
    modal: ModalSpec | null;
    loading: boolean;
    missing: string[];
}

export class ExamplesView extends React.Component<any, ExamplesViewState> {

    constructor(props: any) {
        super(props);
        this.state = {questions: null, prompts: null, index: 0, modal: null, loading: true, missing: []};
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    componentDidMount() {
        const base = `${window.location.protocol}//${window.location.host}`;
        const questionsFile = "example_questions.json";
        const promptsFile = "prompts.json";
        Promise.all([
            this.fetchJson(`${base}/${questionsFile}`),
            this.fetchJson(`${base}/${promptsFile}`)
        ]).then(([questions, prompts]) => {
            const missing: string[] = [];
            if (!questions) missing.push(questionsFile);
            if (!prompts) missing.push(promptsFile);
            this.setState({questions: questions, prompts: prompts, missing: missing, loading: false});
        });
        document.addEventListener("keydown", this.onKeyDown);
    }

    private fetchJson(url: string): Promise<any> {
        return fetch(url)
            .then(val => val.ok ? val.json() : null)
            .catch(() => null);
    }

    componentWillUnmount() {
        document.removeEventListener("keydown", this.onKeyDown);
    }

    private onKeyDown(event: KeyboardEvent) {
        if (event.key === "Escape" && this.state.modal) this.closeModal();
    }

    private move(offset: number) {
        this.setState(prev => {
            const count = prev.questions?.length || 0;
            if (count === 0) return prev;
            return {...prev, index: (prev.index + offset + count) % count, modal: null};
        });
    }

    private openModal(title: string, content: ReactNode, image: boolean = false) {
        this.setState({modal: {title: title, content: content, image: image}});
    }

    private closeModal() {
        this.setState({modal: null});
    }

    private truncate(text: string, limit: number = 32): string {
        const flat = text.trim().replace(/\s+/g, " ");
        return flat.length > limit ? flat.substring(0, limit).trimEnd() + "…" : flat;
    }

    private renderMedia(q: ExampleQuestionDef): ReactNode {
        const audio = q.file.toLowerCase().endsWith(".mp3");
        return (
            <div className="example-media">
                {audio ? this.renderAudio(q) : this.renderImage(q)}
                {this.renderSources(q)}
            </div>
        );
    }

    private renderImage(q: ExampleQuestionDef): ReactNode {
        const url = `/files/${q.file}`;
        const full = <img className="example-full" src={url} alt={q.category}/>;
        return (
            <img className="example-thumb" src={url} alt={q.category}
                 title="Click to enlarge"
                 onClick={() => this.openModal("Image preview", full, true)}/>
        );
    }

    private renderAudio(q: ExampleQuestionDef): ReactNode {
        const source = q.source_urls.length > 0 ? q.source_urls[0] : null;
        const embed = source ? this.youtubeEmbedUrl(source, q.source_time) : null;
        if (embed) {
            return (
                <iframe className="example-video" src={embed} title="YouTube preview"
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen>
                </iframe>
            );
        }
        return <audio className="example-audio" controls src={`/files/${q.file}`}/>;
    }

    private youtubeEmbedUrl(url: string, time?: number[]): string | null {
        const match = url.match(/(?:v=|youtu\.be\/)([\w-]{6,})/);
        if (!match) return null;
        let embed = `https://www.youtube-nocookie.com/embed/${match[1]}`;
        if (time && time.length === 2) embed += `?start=${time[0]}&end=${time[1]}`;
        return embed;
    }

    private renderSources(q: ExampleQuestionDef): ReactNode {
        if (!q.source_urls || q.source_urls.length === 0) return null;
        const links = q.source_urls.map((url, i) =>
            <a key={url} className="example-source-link" href={url} target="_blank" rel="noreferrer">{i + 1}</a>
        );
        return <div className="example-sources"><strong>Sources:</strong>{links}</div>;
    }

    private renderField(label: string, content: ReactNode): ReactNode {
        if (!content) return null;
        return (
            <div className="example-field" key={label}>
                <div className="example-label">{label}</div>
                <div className="example-value">{content}</div>
            </div>
        );
    }

    private renderPrompt(q: ExampleQuestionDef, lang: string): ReactNode {
        const general = this.state.prompts?.[lang]?.[q.category];
        const user = lang === "pl" ? q.prompt_pl : q.prompt_en;
        if (!general && !user) return null;
        const parts: ReactNode[] = [];
        if (general) {
            const title = `General prompt for ${q.category} (${lang.toUpperCase()})`;
            parts.push(
                <span key="general" className="example-link"
                      onClick={() => this.openModal(title, <div className="modal-prewrap">{general}</div>)}>
                    [General prompt for {q.category}]
                </span>
            );
        }
        if (general && user) parts.push(<span key="plus"> + </span>);
        if (user) parts.push(<span key="user" className="example-prompt-text">{user}</span>);
        return parts;
    }

    private renderConditions(q: ExampleQuestionDef): ReactNode {
        return q.conditions.map((cond, i) =>
            <div className="example-condition" key={i}>{this.renderCondition(q, cond)}</div>
        );
    }

    private renderCondition(q: ExampleQuestionDef, cond: ExampleConditionDef): ReactNode {
        switch (cond.type) {
            case "include":
                return this.renderListCondition("Include", cond.expected);
            case "exclude":
                return this.renderListCondition("Exclude", cond.expected);
            case "order":
                return this.renderListCondition("Order", cond.expected);
            case "wacc":
                return this.renderTextCondition("WAcc", String(cond.expected));
            case "ocr":
                return this.renderOcrCondition(cond.expected as string[]);
            case "struct":
                return this.renderStructCondition(q, cond);
            default:
                return <span>{cond.type}</span>;
        }
    }

    private renderListCondition(name: string, expected: any[]): ReactNode {
        const args = (expected || [])
            .map(item => Array.isArray(item) ? `[${item.join(", ")}]` : String(item))
            .join(", ");
        return <span><strong>{name}</strong>{"("}{args}{")"}</span>;
    }

    private renderTextCondition(name: string, text: string): ReactNode {
        const content = <div className="modal-prewrap">{text}</div>;
        return (
            <span>
                <strong>{name}</strong>{"("}
                <span className="example-link" onClick={() => this.openModal(`${name} — expected text`, content)}>
                    {this.truncate(text)}
                </span>
                {")"}
            </span>
        );
    }

    private renderOcrCondition(expected: string[]): ReactNode {
        const blocks = (expected || []).map((text, i) =>
            <div className="ocr-block" key={i}>
                <div className="ocr-block-label">Text block {i + 1}</div>
                <div className="ocr-block-text">{text}</div>
            </div>
        );
        const label = expected && expected.length > 0 ? this.truncate(expected[0]) : "";
        return (
            <span>
                <strong>OCR</strong>{"("}
                <span className="example-link" onClick={() => this.openModal("OCR — expected text blocks", blocks)}>
                    {label}
                </span>
                {")"}
            </span>
        );
    }

    private renderStructCondition(q: ExampleQuestionDef, cond: ExampleConditionDef): ReactNode {
        const schema = q.json_schema?.schema || q.json_schema;
        const schemaNode = schema ? (
            <span className="example-link"
                  onClick={() => this.openModal("JSON Schema", this.jsonView(schema))}>
                JSON Schema
            </span>
        ) : null;
        const outputNode = (
            <span className="example-link"
                  onClick={() => this.openModal("Structured output", this.jsonView(this.parseJson(cond.expected)))}>
                Structured output
            </span>
        );
        return (
            <span>
                <strong>Struct</strong>{"("}
                {schemaNode}{schemaNode ? ", " : null}{outputNode}
                {")"}
            </span>
        );
    }

    private parseJson(value: any): any {
        if (typeof value !== "string") return value;
        try {
            return JSON.parse(value);
        } catch (e) {
            return value;
        }
    }

    private jsonView(value: any): ReactNode {
        if (typeof value === "string") return <div className="modal-prewrap">{value}</div>;
        return <pre className="modal-json">{JSON.stringify(value, null, 2)}</pre>;
    }

    private renderModal(): ReactNode {
        const modal = this.state.modal;
        if (!modal) return null;
        return (
            <div className="modal-overlay" onClick={() => this.closeModal()}>
                <div className={"modal-box" + (modal.image ? " modal-box-image" : "")}
                     onClick={event => event.stopPropagation()}>
                    <div className="modal-header">
                        <span>{modal.title}</span>
                        <button className="modal-close" title="Close" onClick={() => this.closeModal()}>✕</button>
                    </div>
                    <div className="modal-content">{modal.content}</div>
                </div>
            </div>
        );
    }

    private renderUnavailable(): ReactNode {
        const missing = this.state.missing;
        let message: ReactNode;
        if (missing.length > 0) {
            const label = missing.length > 1 ? "files are" : "file is";
            const files = missing.map((file, i) =>
                <React.Fragment key={file}>{i > 0 ? ", " : ""}<code>{file}</code></React.Fragment>
            );
            message = <>Example questions are unavailable — the required data {label} missing: {files}.</>;
        } else {
            message = "No example questions are available.";
        }
        return (
            <div className="examples-section">
                <div className="examples-header">
                    <h2>Example questions</h2>
                </div>
                <div className="example-card example-card-empty">
                    <div className="example-message">{message}</div>
                </div>
            </div>
        );
    }

    render() {
        if (this.state.loading) return null;
        const questions = this.state.questions;
        if (this.state.missing.length > 0 || !questions || questions.length === 0) {
            return this.renderUnavailable();
        }
        const index = Math.min(this.state.index, questions.length - 1);
        const q = questions[index];
        return (
            <div className="examples-section">
                <div className="examples-header">
                    <h2>Example questions</h2>
                    <div className="examples-nav">
                        <button title="Previous question" onClick={() => this.move(-1)}>‹</button>
                        <span className="examples-counter">{index + 1} / {questions.length}</span>
                        <button title="Next question" onClick={() => this.move(1)}>›</button>
                    </div>
                </div>
                <div className="example-card">
                    {this.renderMedia(q)}
                    <div className="example-details">
                        {this.renderField("Category", <span className="example-category">{q.category}</span>)}
                        {this.renderField("Prompt (PL)", this.renderPrompt(q, "pl"))}
                        {this.renderField("Prompt (EN)", this.renderPrompt(q, "en"))}
                        {this.renderField("Verification conditions", this.renderConditions(q))}
                    </div>
                </div>
                {this.renderModal()}
            </div>
        );
    }
}
